import React, { useCallback, useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import Logo from '@images/logo.svg';
import Crest from '@images/crest.svg';
import Link from '@utility/Link';
import InViewport from '@utility/InViewport';
import cn from 'classnames';
import * as styles from './styles';

const Header = ({ invertHeader }) => {
  const data = useStaticQuery(graphql`
    {
      sanityNavigation {
        _rawAboutPages(resolveReferences: { maxDepth: 3 })
        _rawNewPages(resolveReferences: { maxDepth: 3 })
        _rawServicesPages(resolveReferences: { maxDepth: 3 })
        _rawFirstPages(resolveReferences: { maxDepth: 3 })
        _rawSecondPages(resolveReferences: { maxDepth: 3 })
      }
    }
  `);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [fill, setFill] = useState(false);
  const [menuItem, setMenuItem] = useState(null);
  const [scrim, setScrim] = useState(false);

  const escFunction = useCallback((e) => {
    if (e.keyCode === 27) {
      closeMenu();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      currPos.y === 0 ? setFill(false) : setFill(true);
    },
    [fill],
    null,
    false,
    150
  );

  const closeMenu = () => {
    setScrim(false);
    setMenuItem(null);
  };

  const menuHandler = (id) => {
    if (menuItem === id) {
      closeMenu();
    } else {
      setScrim(true);
      setMenuItem(id);
    }
  };

  return (
    <header
      className={cn(styles.header, {
        [styles.fill]: invertHeader || fill
      })}>
      <InViewport className={styles.inner}>
        <div className="relative wrapper text-hotoke flex xl-down:justify-between xl-down:items-center z-[9998]">
          <Link
            className={cn(['opacity-100 hover:opacity-75 transition-opacity duration-300 mr-10'])}
            to={'/'}>
            <Logo />
          </Link>
          <div
            className="xl:hidden relative h-[11px] w-10 cursor-pointer"
            onClick={() => setIsMobileOpen(!isMobileOpen)}>
            <div
              className={cn(
                'absolute top-0 h-[2px] w-full bg-hotoke origin-center transition-transform duration-300',
                {
                  'rotate-45 translate-y-[4.5px]': isMobileOpen
                }
              )}></div>
            <div
              className={cn(
                'absolute bottom-0 h-[2px] w-full bg-hotoke origin-center transition-transform duration-300',
                {
                  '-rotate-45 translate-y-[-4.5px]': isMobileOpen
                }
              )}></div>
          </div>
          <div className={cn(styles.listsWrapper, { [styles.active]: isMobileOpen })}>
            <div class="xl:flex xl:justify-between">
              <ul className={styles.list}>
                <li className={styles.hasDropdown}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    className={cn(styles.dropdown, {
                      [styles.active]: menuItem === 'about'
                    })}
                    onClick={() => menuHandler('about')}>
                    About Us
                  </button>
                  <ul
                    className={cn(styles.dropdownList, {
                      [styles.active]: menuItem === 'about'
                    })}>
                    {data.sanityNavigation._rawAboutPages.page.map((page, index) => (
                      <li key={index}>
                        <Link to={`/${page.page.slug.current}`}>{page.label}</Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className={styles.hasDropdown}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    className={cn(styles.dropdown, {
                      [styles.active]: menuItem === 'class'
                    })}
                    onClick={() => menuHandler('class')}>
                    Classes and Services
                  </button>
                  <ul
                    className={cn(styles.dropdownList, {
                      [styles.active]: menuItem === 'class'
                    })}>
                    {data.sanityNavigation._rawServicesPages.page.map((page, index) => (
                      <li key={index}>
                        <Link to={`/${page.page.slug.current}`}>{page.label}</Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className={styles.hasDropdown}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    className={cn(styles.dropdown, {
                      [styles.active]: menuItem === 'new'
                    })}
                    onClick={() => menuHandler('new')}>
                    I'm New
                  </button>
                  <ul
                    className={cn(styles.dropdownList, {
                      [styles.active]: menuItem === 'new'
                    })}>
                    {data.sanityNavigation._rawNewPages.page.map((page, index) => (
                      <li key={index}>
                        <Link to={`/${page.page.slug.current}`}>{page.label}</Link>
                      </li>
                    ))}
                  </ul>
                </li>
                {data.sanityNavigation._rawFirstPages.map((page, index) => (
                  <li key={index}>
                    <Link to={`/${page.page.slug.current}`}>{page.label}</Link>
                  </li>
                ))}
              </ul>
              <ul className={styles.list}>
                {data.sanityNavigation._rawSecondPages.map((page, index) => (
                  <li key={index}>
                    <Link to={`/${page.page.slug.current}`}>{page.label}</Link>
                  </li>
                ))}
                <li class="xl-down:hidden">
                  <Crest/>
                </li>
              </ul>
            </div>
            <Crest className="xl:hidden"/>
          </div>
        </div>
        <div
          className={cn(
            'fixed inset-0 w-full h-screen opacity-0 transition-opacity duration-500 z-[9997] bg-satori pointer-events-none',
            scrim && 'opacity-75 pointer-events-auto',
          )}
          onClick={() => closeMenu()}></div>
      </InViewport>
    </header>
  );
};

export default Header;
